// components/chat/chat-input/MentionNode.tsx
import {
  DecoratorNode,
  DOMConversionMap,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from "lexical";
import { getStudyToolById, getIconColorForItem } from "./studyToolsData";
import { AtSign } from "lucide-react";

export type SerializedMentionNode = Spread<
  {
    toolId: string;
    label: string;
    iconColor?: string;
  },
  SerializedLexicalNode
>;

export class MentionNode extends DecoratorNode<JSX.Element> {
  __toolId: string;
  __label: string;
  __iconColor?: string;

  static getType(): string {
    return "mention";
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__toolId, node.__label, node.__iconColor, node.__key);
  }

  constructor(toolId: string, label: string, iconColor?: string, key?: NodeKey) {
    super(key);
    this.__toolId = toolId;
    this.__label = label;
    this.__iconColor = iconColor;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement("span");
    span.className = "mention-node";
    return span;
  }

  updateDOM(): false {
    return false;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("span");
    element.textContent = `@${this.__label}`;
    element.className = "mention-node";
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return null;
  }

  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    return $createMentionNode(serializedNode.toolId, serializedNode.label, serializedNode.iconColor);
  }

  exportJSON(): SerializedMentionNode {
    return {
      type: "mention",
      version: 1,
      toolId: this.__toolId,
      label: this.__label,
      iconColor: this.__iconColor,
    };
  }

  getTextContent(): string {
    return `@${this.__label}`;
  }

  getToolId(): string {
    return this.__toolId;
  }

  getLabel(): string {
    return this.__label;
  }

  decorate(): JSX.Element {
    const tool = getStudyToolById(this.__toolId);
    const Icon = tool?.icon || AtSign;
    const iconColor = this.__iconColor || tool?.iconColor || "hsl(var(--primary))";

    return (
      <span
        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-sm font-medium align-baseline"
        style={{
          backgroundColor: `color-mix(in srgb, ${iconColor} 15%, transparent)`,
          color: iconColor,
        }}
      >
        <Icon className="h-3.5 w-3.5" />
        <span>{this.__label}</span>
      </span>
    );
  }

  isInline(): boolean {
    return true;
  }
}

export function $createMentionNode(toolId: string, label: string, iconColor?: string): MentionNode {
  return new MentionNode(toolId, label, iconColor);
}

export function $isMentionNode(
  node: LexicalNode | null | undefined
): node is MentionNode {
  return node instanceof MentionNode;
}
